import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChatCircleDots, X, PaperPlaneRight, ArrowClockwise, Lightning } from '@phosphor-icons/react';
import API from '@/lib/api';

const QUICK_PROMPTS = [
  { icon: '📄', label: 'Hoe maak ik een factuur?', q: 'Hoe maak ik een factuur stap voor stap?' },
  { icon: '🛒', label: 'B2B shop activeren', q: 'Hoe activeer ik de B2B shop voor mijn klanten?' },
  { icon: '📦', label: 'Artikels importeren', q: 'Hoe importeer ik mijn artikels via CSV?' },
  { icon: '💰', label: 'Betaling matchen', q: 'Hoe match ik een bankafschrift met een openstaande factuur?' },
  { icon: '🧾', label: 'BTW-aangifte', q: 'Hoe genereer ik mijn BTW-aangifte voor een kwartaal?' },
];

/**
 * 🤖 Drijvende AI-helpknop (BillBot) — rechtsonder op alle pagina's
 * Klik opent een chat-modal met Claude Sonnet 4.6 die BillFac-context kent.
 */
export default function HelpWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Hoi! Ik ben **BillBot** 🤖 — je AI-assistent voor BillFac.\n\nWaar mag ik je mee helpen? Tip: klik op een snelvraag hieronder of typ je eigen vraag.' }
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const endRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll naar onder bij nieuw bericht
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  // Focus input bij openen
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setSending(true);
    try {
      const r = await API.post('/help/chat', {
        message: text,
        session_id: sessionId,
        page_path: location.pathname,
        page_titel: document.title || '',
      });
      const d = r.data || {};
      if (d.session_id) setSessionId(d.session_id);
      setMessages((m) => [...m, { role: 'assistant', text: d.reply || '...' }]);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Sorry, er ging iets mis. Probeer het over enkele seconden opnieuw.';
      setMessages((m) => [...m, { role: 'assistant', text: `⚠️ ${msg}`, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const resetChat = () => {
    setSessionId('');
    setMessages([{ role: 'assistant', text: 'Nieuwe sessie gestart. Waar mag ik je mee helpen? 🤖' }]);
  };

  // Render markdown-light: **bold**, line breaks
  const renderText = (text) => {
    return text.split('\n').map((line, i) => (
      <div key={i} className={line.trim() === '' ? 'h-2' : ''}>
        {line.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
          part.startsWith('**') && part.endsWith('**') ?
            <strong key={j}>{part.slice(2, -2)}</strong> :
            <span key={j}>{part}</span>
        )}
      </div>
    ));
  };

  return (
    <>
      {/* Drijvende knop */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-[#002FA7] to-[#5B21B6] hover:scale-105 active:scale-95 transition-transform shadow-xl flex items-center justify-center rounded-full text-white"
          title="Hulp nodig? Vraag het BillBot"
          data-testid="help-widget-toggle"
          aria-label="Open BillBot help-assistent"
        >
          <ChatCircleDots size={26} weight="fill" />
          <span className="absolute top-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse"></span>
        </button>
      )}

      {/* Chat-modal */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-3rem)] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200" data-testid="help-widget-panel">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#002FA7] to-[#5B21B6] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <ChatCircleDots size={20} weight="fill" />
              </div>
              <div>
                <div className="font-bold text-sm">BillBot</div>
                <div className="text-[11px] text-white/80 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span> AI-assistent · Online
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={resetChat} title="Nieuwe conversatie" className="p-1.5 hover:bg-white/20 rounded transition-colors" data-testid="help-widget-reset">
                <ArrowClockwise size={16} />
              </button>
              <button onClick={() => setOpen(false)} title="Sluiten" className="p-1.5 hover:bg-white/20 rounded transition-colors" data-testid="help-widget-close">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-[13px] leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-[#002FA7] text-white rounded-tr-sm'
                    : m.error
                      ? 'bg-red-50 text-red-700 border border-red-200 rounded-tl-sm'
                      : 'bg-white text-gray-800 border border-gray-200 rounded-tl-sm'
                }`}>
                  {renderText(m.text)}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3 py-2.5 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Quick prompts — alleen tonen bij eerste interactie */}
          {messages.length <= 1 && (
            <div className="px-3 py-2 border-t border-gray-100 bg-white">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5 flex items-center gap-1"><Lightning size={10} weight="fill" /> Snelvragen</div>
              <div className="flex flex-wrap gap-1">
                {QUICK_PROMPTS.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => send(p.q)}
                    disabled={sending}
                    className="text-[11px] px-2 py-1 bg-gray-100 hover:bg-[#002FA7] hover:text-white text-gray-700 rounded-full transition-colors"
                    data-testid={`help-quickprompt-${i}`}
                  >
                    {p.icon} {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-gray-200 bg-white p-2.5 flex items-center gap-2"
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Stel een vraag over BillFac..."
              disabled={sending}
              className="flex-1 bg-gray-50 border border-gray-200 rounded-full px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] disabled:opacity-50"
              maxLength={2000}
              data-testid="help-widget-input"
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              className="w-9 h-9 bg-[#002FA7] hover:bg-[#001f7a] disabled:bg-gray-300 text-white rounded-full flex items-center justify-center transition-colors disabled:cursor-not-allowed"
              data-testid="help-widget-send"
            >
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
